import {FC, useMemo, useCallback, useRef, useEffect, useState} from "react";
import {Box, CircularProgress} from "@mui/material";
import {useQueryClient} from "@tanstack/react-query";
import {App2AnnouncementVideoPlayer} from "./App2AnnouncementVideoPlayer";
import {
	useGetLearnerAssignedAnnouncements,
	useFlushAnnouncementVideoProgress,
	ANNOUNCEMENT_VIDEO_PROGRESS_SOCKET_EVENT,
	ANNOUNCEMENTS_QUERY_KEYS,
	mergeAnnouncementVideoTimeSeconds,
	patchLearnerAssignedEnrollmentVideoTime,
	patchLearnerNotificationEnrollmentVideoTime,
	type LearnerAssignedAnnouncementDto,
	type AnnouncementVideoProgressMessage
} from "@/app2/shared/data";
import {useWebSocket} from "@/app2/shared/contexts/WebSocketContext";
import {
	readStoredAnnouncementVideoTime,
	writeStoredAnnouncementVideoTime
} from "./announcementVideoTimeStorage";

interface IProps {
	announcement: LearnerAssignedAnnouncementDto;
}

/**
 * App2 announcement video with persisted playback (WebSocket + learner-assigned videoTime).
 */
export const App2AnnouncementPageContent: FC<IProps> = ({announcement: initialAnnouncement}) => {
	const {socket} = useWebSocket();
	const queryClient = useQueryClient();
	const {data, refetch} = useGetLearnerAssignedAnnouncements();
	const flushAnnouncementVideoProgress = useFlushAnnouncementVideoProgress();
	const lastProgressRef = useRef<number | null>(null);
	const [hydrated, setHydrated] = useState(false);
	/** Frozen once after hydration; useState so the player receives currentTime on re-render. */
	const [resumeTimeForPlayer, setResumeTimeForPlayer] = useState<number | undefined>(undefined);

	const enrollmentId = initialAnnouncement.id;

	useEffect(() => {
		let cancelled = false;
		setHydrated(false);
		setResumeTimeForPlayer(undefined);
		const cachedBeforeRefetch = queryClient
			.getQueriesData<{data?: LearnerAssignedAnnouncementDto[]}>({
			queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED]
		})
			.map(([, value]) => value?.data?.find((item) => item.id === enrollmentId))
			.find(Boolean);
		const storedVideoTime = readStoredAnnouncementVideoTime(enrollmentId);
		void refetch().then((result) => {
			if (cancelled) {
				return;
			}
			const row = result.data?.data?.find((item) => item.id === enrollmentId);
			const mergedVideoTime = mergeAnnouncementVideoTimeSeconds(
				row?.videoTime,
				cachedBeforeRefetch?.videoTime,
				initialAnnouncement.videoTime,
				storedVideoTime
			);
			if (mergedVideoTime != null) {
				patchLearnerAssignedEnrollmentVideoTime(queryClient, enrollmentId, mergedVideoTime);
				patchLearnerNotificationEnrollmentVideoTime(queryClient, enrollmentId, mergedVideoTime);
				writeStoredAnnouncementVideoTime(enrollmentId, mergedVideoTime);
			}
			setResumeTimeForPlayer(mergedVideoTime);
			setHydrated(true);
		});
		return () => {
			cancelled = true;
			setHydrated(false);
			setResumeTimeForPlayer(undefined);
		};
	}, [enrollmentId, refetch, initialAnnouncement.videoTime, queryClient]);

	const announcement = useMemo(() => {
		const list = data?.data;
		if (!list) {
			return initialAnnouncement;
		}
		const row = list.find((item) => item.id === enrollmentId) ?? initialAnnouncement;
		const mergedVideoTime = mergeAnnouncementVideoTimeSeconds(
			row.videoTime,
			initialAnnouncement.videoTime,
			resumeTimeForPlayer,
			readStoredAnnouncementVideoTime(enrollmentId)
		);
		if (mergedVideoTime === row.videoTime) {
			return row;
		}
		return {...row, videoTime: mergedVideoTime ?? null};
	}, [data?.data, enrollmentId, initialAnnouncement, resumeTimeForPlayer]);

	/** Keep last position for unmount flush; emit via WebSocket when available (same pattern as lesson video). */
	const emitProgress = useCallback(
		(progress: number) => {
			if (!enrollmentId || progress == null || Number.isNaN(progress)) {
				return;
			}
			lastProgressRef.current = progress;
			writeStoredAnnouncementVideoTime(enrollmentId, progress);
			if (!socket?.emit) {
				return;
			}
			const message: AnnouncementVideoProgressMessage = {
				enrollmentId,
				progress
			};
			socket.emit(ANNOUNCEMENT_VIDEO_PROGRESS_SOCKET_EVENT, message);
		},
		[enrollmentId, socket]
	);

	const handleVideoProgress = useCallback(
		(e: {playedSeconds: number}) => {
			emitProgress(e.playedSeconds);
		},
		[emitProgress]
	);

	const handlePlaybackTime = useCallback(
		(seconds: number) => {
			lastProgressRef.current = seconds;
			writeStoredAnnouncementVideoTime(enrollmentId, seconds);
		},
		[enrollmentId]
	);

	const handleEnded = useCallback(() => {
		if (lastProgressRef.current != null) {
			emitProgress(lastProgressRef.current);
		}
	}, [emitProgress]);

	useEffect(() => {
		return () => {
			if (lastProgressRef.current != null && enrollmentId) {
				writeStoredAnnouncementVideoTime(enrollmentId, lastProgressRef.current);
				flushAnnouncementVideoProgress({
					enrollmentId,
					progress: lastProgressRef.current
				});
			}
		};
	}, [enrollmentId, flushAnnouncementVideoProgress]);

	if (!announcement.videoUrl) {
		return null;
	}

	if (!hydrated) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
				<CircularProgress size={32} />
			</Box>
		);
	}

	return (
		<App2AnnouncementVideoPlayer
			announcement={{
				videoUrl: announcement.videoUrl,
				videoType: announcement.videoType,
				videoThumbnailUrl: announcement.videoThumbnailUrl,
				complete: announcement.complete
			}}
			currentTime={resumeTimeForPlayer}
			onPlay={() => undefined}
			onPause={() => undefined}
			onProgress={handleVideoProgress}
			onPlaybackTime={handlePlaybackTime}
			onEnded={handleEnded}
		/>
	);
};
